import { useRef } from "react"

import AppShell from "@/components/tuw/AppShell"
import InsiderSupportCard from "@/components/tuw/insider/InsiderSupportCard"
import { Announcement, BeforeYouSubscribe, HowItWorks } from "@/components/tuw/insider/InsiderMarketing"
import { Canceled, Closed, MemberHead, PastDue, Unavailable } from "@/components/tuw/insider/InsiderStates"
import ManageCard from "@/components/tuw/insider/ManageCard"
import OfferHero from "@/components/tuw/insider/OfferHero"
import PriceComparison from "@/components/tuw/insider/PriceComparison"
import SavingsCalculator from "@/components/tuw/insider/SavingsCalculator"
import StickyBuyBar from "@/components/tuw/insider/StickyBuyBar"
import WelcomeFlash from "@/components/tuw/insider/WelcomeFlash"

// The Insider membership / price-lock hub. One of seven states from
// CustomerInsiderPresenter picks the composition; every price, date and the
// plan amount come from the presenter, never from the design's sample data.
//
//   offer       → hero with price card · savings calculator · price tables ·
//                 how it works · before you subscribe (price card + FAQ) ·
//                 floating Subscribe bar
//   welcome     → welcome strip + the member view
//   member      → head · announcement · your rates · manage
//   past_due    → state card (update payment) · your rates
//   canceled    → state card
//   closed      → state card
//   unavailable → empty card
//
// enrollment.closesAt is null while enrollment has no end date; the countdown
// and every "closes on" line then stay hidden.
function Offer({ plan, comparison, endpoints, closesAt, wrapRef }) {
  const heroRef = useRef(null)
  const closeRef = useRef(null)
  return (
    <>
      <OfferHero plan={plan} comparison={comparison} endpoints={endpoints} closesAt={closesAt} heroRef={heroRef} />
      <SavingsCalculator comparison={comparison} plan={plan} />
      <PriceComparison comparison={comparison} subscriber={false} />
      <HowItWorks plan={plan} />
      <BeforeYouSubscribe plan={plan} comparison={comparison} endpoints={endpoints} closesAt={closesAt} closeRef={closeRef} />
      <StickyBuyBar
        plan={plan}
        endpoints={endpoints}
        closesAt={closesAt}
        heroRef={heroRef}
        closeRef={closeRef}
        wrapRef={wrapRef}
      />
    </>
  )
}

function Body({ insider, wrapRef }) {
  const { state = "offer", plan, subscription, comparison, enrollment, endpoints } = insider ?? {}

  switch (state) {
    case "unavailable":
      return <Unavailable />
    case "closed":
      return <Closed closesAt={enrollment?.closesAt} />
    case "canceled":
      return <Canceled subscription={subscription} />
    case "past_due":
      return (
        <>
          <PastDue endpoints={endpoints} />
          <PriceComparison comparison={comparison} subscriber />
        </>
      )
    case "welcome":
    case "member":
      return (
        <>
          {state === "welcome" && <WelcomeFlash />}
          <MemberHead subscription={subscription} />
          <Announcement />
          <PriceComparison comparison={comparison} subscriber />
          <ManageCard subscription={subscription} endpoints={endpoints} />
        </>
      )
    default:
      return (
        <Offer plan={plan} comparison={comparison} endpoints={endpoints} closesAt={enrollment?.closesAt} wrapRef={wrapRef} />
      )
  }
}

export default function Insider({ insider }) {
  const wrapRef = useRef(null)
  return (
    <div className="min-w-0 flex-1">
      <div
        ref={wrapRef}
        className="flex max-w-[1120px] flex-col gap-[18px] px-[30px] pt-[22px] pb-[60px] max-[861px]:gap-[14px] max-[861px]:px-[14px] max-[861px]:pt-[16px] max-[861px]:pb-[48px]"
      >
        <Body insider={insider} wrapRef={wrapRef} />
        <InsiderSupportCard />
      </div>
    </div>
  )
}

Insider.layout = (page) => (
  <AppShell active="Insider" crumb={["Home", "Insider"]}>
    {page}
  </AppShell>
)
